// Copyright (c) 2015-present, salesforce.com, inc. All rights reserved

/* eslint-disable no-unused-expressions, no-sequences, no-restricted-globals, id-length, prefer-destructuring, prefer-spread, eslint-comments/no-unlimited-disable, consistent-return, max-len, no-param-reassign, no-prototype-builtins, no-restricted-syntax, no-shadow, no-underscore-dangle, prefer-rest-params */
/* global document, localStorage, window */

const CONST = {
  ACTIVITY_FREQ: 1000 * 30,
  ADRUM_APP_KEY: 'mc-lib-adrum-app-key',
  ADRUM_URL: 'mc-lib-adrum-url',
  DEFAULT_TIMEOUT: 5000,
  HANDSHAKE_MSG: 'MCLIB.HANDSHAKE',
  HANDSHAKE_REPLY_MSG: 'MCLIB.HANDSHAKE_REPLY',
  INIT_MSG: 'initData',
  LOG_PERCENT_STORAGE_KEY: 'mc-lib-log-percent',
  VERSION: '4.0.7'
};

let log = () => {};
let warn = () => {};

const bind = function(func, context) {
  // storing args other than func and context at time of bind
  const args = Array.prototype.slice.call(arguments, 2);

  return function() {
    // add any arguments passed in at the time of execution
    // to the initially bound arguments.
    const combinedArgs = args.concat(Array.prototype.slice.call(arguments));

    return func.apply(context || this, combinedArgs);
  };
};

const throttle = function(func, wait, context) {
  let lastCall = 0;
  let timeout = null;

  const throttledFunc = function() {
    clearTimeout(timeout);

    const now = new Date().getTime();

    if (now > lastCall + wait) {
      lastCall = now;
      return func.apply(context || this, arguments);
    }
    timeout = setTimeout(throttledFunc, lastCall + (wait - now));
  };

  return throttledFunc;
};

const map = function(obj, cb) {
  if (Array.prototype.map) {
    return Array.prototype.map.call(obj, cb);
  }

  const clone = [];

  for (let i = 0, ii = obj.length; i < ii; i++) {
    // passing array item, array, and index into callback
    clone[i] = cb(obj[i], obj, i);
  }

  return clone;
};

const addEventListener = function(eventName, callback, context) {
  let eventMethod;

  // Create IE + others compatible event handler
  if (window.addEventListener) {
    eventMethod = 'addEventListener';
  } else {
    eventMethod = 'attachEvent';
    eventName = `on${eventName}`;
  }

  const eventer = window[eventMethod];
  const boundCallback = bind(callback, context);

  // Listen to message from parent window
  if (eventer) {
    eventer(eventName, boundCallback, false);
  } else {
    warn('Could not listen for messages on window %o', window);
    return false;
  }
};

const isIframe = function() {
  try {
    return window.self !== window.top;
  } catch (error) {
    return true;
  }
};

const isChromeDevTools = (data = {}) => data.source === '@devtools-page';

const containsItem = function(object, target) {
  let response = false;
  // object should be array, target should be string
  if (!object) {
    return response;
  }

  map(object, item => {
    // check if the item is a RegExp - logic taken from underscorejs
    if (Object.prototype.toString.call(item) === '[object RegExp]') {
      response = item.test(target);
    } else if (item === target) {
      response = true;
    }
  });

  return response;
};

const filterItems = function(obj, predicate, context) {
  const results = [];
  const nativeFilter = Array.prototype.filter;

  if (Boolean(obj) !== true) {
    return results;
  }

  if (nativeFilter && obj.filter === nativeFilter) {
    return obj.filter(predicate, context);
  }

  obj.forEach((value, index, list) => {
    if (predicate.call(context || null, value, index, list)) {
      results.push(value);
    }
  });

  return results;
};

const resolveOrigin = function(url) {
  // Wildcard only allowed for local development, not to be used in prod
  if (url === '*') {
    warn('Using wildcard origin');

    return url;
  }

  const a = document.createElement('a');
  a.href = url || window.location.origin;

  // Use a.origin if possible
  if (a.origin) return a.origin;

  // Otherwise, build an origin. Default to window if not available
  const protocol =
    a.protocol.length > 4 ? a.protocol : window.location.protocol;
  const host = a.host.length
    ? a.port === '80' || a.port === '443'
      ? a.hostname
      : a.host
    : window.location.host; // eslint-disable-line no-nested-ternary
  return `${protocol}//${host}`;
};

const MCLib = function(options = {}) {
  // Check whether logging is enabled (via localStorage) and logging methods exist
  const loggingEnabled =
    Boolean(localStorage) && localStorage.getItem('enableLogging') === 'true';

  /* eslint-disable no-console */
  if (loggingEnabled && console) {
    log = (...args) => console.log('MCLib Log:', ...args);
    if (console.warn) {
      warn = (...args) => console.warn('MCLib Warn:', ...args);
    }
  }
  /* eslint-enable no-console */

  if (window.__mclibinstance === undefined) {
    window.__mclibinstance = this;
  } else {
    if (window.__mclibinstance.version !== CONST.VERSION) {
      warn(
        `Unable to load MC-Lib version ${
          CONST.VERSION
        }. An instance with version ${
          window.__mclibinstance.version
        } already exists.`
      );
    } else {
      warn(
        'Attempting to create an additional instance of MC-Lib when an instance already exists.'
      );
    }
    return window.__mclibinstance;
  }

  this.allowedOrigin = null;
  this.analyticsVerified = null;
  this.currentAppData = null;
  this.handlers = [];
  this.handshakeComplete = false;
  this.lastUrl = null;
  this.mc = null;
  this.name = window.name || '';
  this.origin = resolveOrigin();
  this.pendingRequests = [];
  this.requestHandlers = {};
  this.requestId = 1;
  this.timeOuts = {};
  this.verifiedMC = null;
  this.version = CONST.VERSION;

  this.options = {
    allowedOrigin: null,
    eventCategory: '',
    googleAnalyticsId: null,
    initAppDynamics: true,
    initData: true,
    initTimeout: 1000,
    logErrors: false,
    mc: window.parent,
    trackActivity: true,
    trackUrl: true,
    ...options
  };

  this.allowedOrigin = resolveOrigin(this.options.allowedOrigin);

  if (isIframe()) {
    this.mc = this.options.mc || null;
    this.listen();

    if (this.options.trackActivity) {
      this.trackActivity();
    }

    if (this.options.trackUrl) {
      this.trackUrl();
    }

    if (this.options.logErrors) {
      window.onerror = bind(function(message, file, line) {
        this.logEvent('error', `${file}(${line}): ${message}`);
      }, this);
    }

    if (this.options.initAppDynamics) {
      this.initAppDynamics();
    }

    if (this.options.googleAnalyticsId) {
      this.initGoogleAnalytics(this.options.googleAnalyticsId);
    }

    // Handshake must be completed first, followed by initData (which is called after handshake completes)
    if (this.options.initData) {
      this.handshake();
    } else {
      warn(
        'The initData option was passed as false. You must manually call handshake() to initiate.'
      );
    }

    this.verifyAnalytics();
    this.unload();
  } else {
    log('Loaded outside of iframe, MCLib is disabled.');
    this.verifiedMC = false;
    this.callInitCallback();
  }
};

MCLib.prototype.on = function(name, targets, func, context) {
  let i;

  if (name.indexOf(' ') !== -1) {
    name = name.split(' ');
  } else {
    name = [name];
  }

  if (typeof targets === 'string') {
    targets = [targets];
  }

  for (i = 0; i < name.length; i++) {
    this.handlers.push({
      name: name[i] || null,
      targets: targets || null,
      func: func || null,
      context: context || null
    });
  }
};

MCLib.prototype.off = function(name, func, context) {
  this.handlers = filterItems(
    this.handlers,
    handler =>
      (!name || handler.name !== name) &&
      (!func || handler.func !== func) &&
      (!context || handler.context !== context),
    this
  );
};

MCLib.prototype.postMessageMC = function(
  data,
  callback,
  fallbackResponse,
  timeout
) {
  data.from = this.name;
  data.version = CONST.VERSION;
  data.requestId = data.requestId || this.requestId++;
  timeout = timeout || CONST.DEFAULT_TIMEOUT;
  data.fallbackResponse = fallbackResponse || null;
  data.eventCategory = data.eventCategory || this.options.eventCategory;

  if (this.verifiedMC === false) {
    // if the MC has been verified as not present, always run the outside MC message
    if (callback) {
      this.requestHandlers[data.requestId] = callback;
      this.processOutsideMsg(data);
    }
  } else if (
    this.verifiedMC === null &&
    data.msg !== CONST.INIT_MSG &&
    data.msg !== CONST.HANDSHAKE_MSG
  ) {
    // if the MC hasn't been verified and this isn't the init call, set the request to pending
    this.pendingRequests.push(arguments);
  } else {
    // the MC has been verified or this is the init call, set up the timeout and make the request
    if (callback) {
      this.requestHandlers[data.requestId] = callback;

      // set an immediate timeout around setting the timeout in order to keep this from running until the current
      // stack has processed. Otherwise we get instances where the timeout can fire before the message is actually
      // posted.
      setTimeout(
        bind(function() {
          if (this.requestHandlers[data.requestId]) {
            this.timeOuts[data.requestId] = setTimeout(
              bind(this.processOutsideMsg, this, data),
              timeout
            );
          }
        }, this),
        0
      );
    }

    if (this.mc) {
      this.mc.postMessage(JSON.stringify(data), this.allowedOrigin);
      log(`postMessageMC ${JSON.stringify(data)}`);
    }
  }
};

MCLib.prototype.processMessage = function(data) {
  if (data && data.msg) {
    log('Processing message', data.msg);

    if (data.requestId && this.requestHandlers[data.requestId]) {
      // clearing timeout
      window.clearTimeout(this.timeOuts[data.requestId]);
      delete this.timeOuts[data.requestId];

      this.requestHandlers[data.requestId](data.data);

      // deleting request handler for that request id
      delete this.requestHandlers[data.requestId];
    }

    this.trigger(data.msg, data.from, data, true);
  }
};

MCLib.prototype.trigger = function(
  eventToTrigger,
  origin,
  eventData,
  internal
) {
  const data = { msg: eventToTrigger, from: origin };

  for (let i = 0, ii = this.handlers.length; i < ii; i++) {
    if (
      this.handlers[i].name === data.msg &&
      containsItem(this.handlers[i].targets, data.from) &&
      typeof this.handlers[i].func === 'function'
    ) {
      this.handlers[i].func.call(this.handlers[i].context, eventData);
    }
  }
  if (!internal) {
    log(
      'Trigger',
      'arbitrary event -> ',
      JSON.stringify(data),
      'triggered with -> ',
      JSON.stringify(eventData)
    );
  }
};

MCLib.prototype.processOutsideMsg = function(data) {
  // calling stored callback
  const response = data.fallbackResponse || { err: true };

  if (Object.prototype.toString.call(response) === '[object Object]') {
    response.msg = 'MCLib disabled. App is not loaded inside ETMC.';
  }

  if (this.requestHandlers[data.requestId]) {
    this.requestHandlers[data.requestId].call(this, response);
  }

  // deleting stored callback and timeout id
  delete this.requestHandlers[data.requestId];
  delete this.timeOuts[data.requestId];
};

MCLib.prototype.validateMessage = function(event) {
  try {
    log('Validating message', JSON.stringify(event.data));

    const source = event.source; // Must equal the expected mc-app source (defaults to window.parent)
    const origin = resolveOrigin(event.origin); // Must be the mc-app location (passed via options)
    let data = event.data; // The actual message

    if (isChromeDevTools(data)) {
      return;
    }

    if (source !== this.mc) {
      throw new Error('Invalid source');
    }

    if (this.allowedOrigin !== '*' && origin !== this.allowedOrigin) {
      throw new Error('Invalid origin');
    }

    // Try to parse the data if it has been stringified
    if (typeof data === 'string') {
      data = JSON.parse(data);
    }

    if (!data || !data.msg) {
      throw new Error('Invalid message');
    } else if (data.msg === CONST.HANDSHAKE_REPLY_MSG) {
      log('Handshake complete.');
      this.handshakeComplete = true;

      // initData is a required call to verify the presence of the MC
      // if we're already verified we don't need to re-run
      if (!this.verifiedMC) this.initData();
    } else if (this.handshakeComplete) {
      this.processMessage(data);
    }
  } catch (err) {
    warn(err);

    // this is returned for tests only
    return err;
  }
};

MCLib.prototype.listen = function() {
  const messageEvent = window.onmessage ? 'onmessage' : 'message';
  addEventListener(messageEvent, this.validateMessage, this);
  log('Listening....');
};

MCLib.prototype.updateActivity = function() {
  this.postMessageMC({ msg: 'activity' });
};

MCLib.prototype.trackActivity = function() {
  const activityHandler = throttle(
    this.updateActivity,
    CONST.ACTIVITY_FREQ,
    this
  );

  addEventListener('mousemove', activityHandler);
  addEventListener('keypress', activityHandler);
};

MCLib.prototype.updateUrl = function(checkpoint, forceSend) {
  const url = window.location.href;

  if (url !== this.lastUrl || Boolean(forceSend)) {
    this.lastUrl = url;
    this.postMessageMC({
      msg: 'urlChange',
      url: this.lastUrl,
      checkpoint: Boolean(checkpoint)
    });
  }
};

MCLib.prototype.trackUrl = function() {
  const pushState = window.history.pushState;

  this.updateUrl();

  if (pushState) {
    // wrap pushState, there's no event to track this
    window.history.pushState = bind(function() {
      pushState.apply(window.history, arguments);
      this.updateUrl();
    }, this);
  }

  addEventListener('popstate', this.updateUrl, this);
  addEventListener('hashchange', this.updateUrl, this);

  this.on(
    'requestUrl',
    ['marketing-cloud'],
    function() {
      this.updateUrl(false, true);
    },
    this
  );
};

MCLib.prototype.callInitCallback = function(response) {
  if (typeof this.options.callback === 'function') {
    this.options.callback(this.verifiedMC, response);
  }
};

MCLib.prototype.handshake = function() {
  this.handshakeComplete = false;

  // We don't need to do handle anything except errors on the callback for
  // this message as the rest is covered under validateMessage
  this.postMessageMC(
    { msg: CONST.HANDSHAKE_MSG },
    bind(function(response) {
      if (!this.handshakeComplete && response && response.err === true) {
        this.verifiedMC = false;
        this.callInitCallback(response);
      }
    }, this),
    null,
    this.options.initTimeout
  );
};

MCLib.prototype.initData = function() {
  this.getInitData(
    bind(function(response) {
      // if this response returns err then we've hit the timeout response
      // when that happens, the MC is not verified and calls will be immediately returned as such
      this.verifiedMC = response.err !== true;

      // loop through any requests that were made while the init message was processing and run them accordingly
      while (this.pendingRequests.length) {
        this.postMessageMC.apply(this, this.pendingRequests.pop());
      }

      this.callInitCallback(response);
    }, this),
    this.options.initTimeout
  );
};

MCLib.prototype.getInitData = function(callback, timeout) {
  this.postMessageMC(
    { msg: CONST.INIT_MSG },
    bind(function(response) {
      this.currentAppData = response;

      if (callback && typeof callback === 'function') {
        callback(response);
      }
    }, this),
    null,
    timeout
  );
};

MCLib.prototype.allowsReturn = function(callback) {
  this.getInitData(response => {
    const fromAppData =
      response.deepLinkData && response.deepLinkData.fromAppData
        ? response.deepLinkData.fromAppData
        : null;

    callback(
      response.deepLinkData && response.deepLinkData.allowReturn,
      fromAppData
    );
  });
};

MCLib.prototype.getHost = function(callback) {
  // setting up fallback response
  const fallbackResponse = this.allowedOrigin;

  this.postMessageMC({ msg: 'getHost' }, callback, fallbackResponse, 200);
};

MCLib.prototype.appDeeplink = function(options, callback) {
  callback =
    callback ||
    function(options) {
      // if options.link and it's not equal to '/',
      // set the location of the current window to the deep link location,
      // otherwise, set it to the app url of the current app from currentAppData
      if (Boolean(options.link) && options.link !== '/') {
        window.location.replace(options.link);
      } else {
        window.location.replace(this.currentAppData.applicationUrl);
      }
    };

  if (typeof options !== 'object') {
    warn('Valid options must be provided');
    return false;
  }

  if (this.currentAppData && options.returnDeepLink) {
    options.allowReturn = true;
  }

  const isCurrentAppOptionsApp =
    Boolean(this.currentAppData) &&
    typeof options.appName !== 'undefined' &&
    this.currentAppData.name === options.appName;

  if (isCurrentAppOptionsApp) {
    callback(options);
    return;
  }

  const data = {
    msg: 'appdeeplink',
    options
  };

  this.postMessageMC(data);
};

MCLib.prototype.appDeepLink = MCLib.prototype.appDeeplink;

MCLib.prototype.returnToPreviousApp = function() {
  const callback = bind(function(response) {
    let options;

    if (response.deepLinkData && response.deepLinkData.allowReturn) {
      options = {
        appName: response.deepLinkData.fromAppData.name,
        link: response.deepLinkData.returnDeepLink
      };

      this.appDeepLink(options);
    } else {
      warn('No return path provided.');
    }
  }, this);

  // Always fetch the newest app data before peforming a returnToApp
  this.getInitData(callback);
};

MCLib.prototype.hasAccess = function(object, operation, callback) {
  if (!object || !operation) {
    warn('An object and operation must be provided.');
    return false;
  }

  const data = {
    msg: 'hasaccess',
    object,
    operation
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.hasAccessMulti = function(permissions, callback) {
  if (!permissions) {
    warn('Permissions must be provided.');
    return false;
  }

  const data = {
    msg: 'hasaccessMulti',
    permissions
  };

  if (this.verifiedMC === false) {
    callback(permissions);
  } else {
    this.postMessageMC(data, callback);
  }
};

MCLib.prototype.getPermissionSet = function(object, callback) {
  if (!object) {
    warn('An object and operation must be provided.');
    return false;
  }

  const data = {
    msg: 'getpermissionset',
    object
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.getProvisionedApplications = function(callback) {
  const data = {
    msg: 'getProvisionedApplications'
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.registerAllowedOrigin = function(origin) {
  if (!origin) {
    warn(
      'You must provide an origin you wish to allow when calling registerAllowedOrigin.'
    );
    return;
  }

  const allowedOrigin = resolveOrigin(origin);
  this.postMessageMC({ msg: 'registerAllowedOrigin', allowedOrigin });
};

MCLib.prototype.registerAppHelp = function(helpMenu, appId, callback) {
  if (
    !helpMenu ||
    Object.prototype.toString.call(helpMenu) !== '[object Array]'
  ) {
    warn(
      'helpMenu must be an array and provided when registering a help menu.'
    );
    return;
  }

  if (!appId || Object.prototype.toString.call(appId) !== '[object String]') {
    warn('You must provide an appId when registering a help menu.');
    return;
  }

  const data = {
    appId,
    msg: 'registerAppHelp',
    object: helpMenu
  };

  this.postMessageMC(data, callback, { stored: false });
};

MCLib.prototype.registerAppMenu = function(menu, appId, callback) {
  if (!menu || Object.prototype.toString.call(menu) !== '[object Array]') {
    warn('Menu must be an array and provided when registering a menu.');
    return;
  }

  if (!appId || Object.prototype.toString.call(appId) !== '[object String]') {
    warn('You must provide an appId when registering a menu.');
    return;
  }

  const data = {
    msg: 'registerAppMenu',
    object: menu,
    appId
  };

  this.postMessageMC(data, callback, { stored: false });
};

MCLib.prototype.registerAppNotifications = function(
  notifications,
  appId,
  callback
) {
  // loop variable
  let current;

  // loop variables
  let i;
  let j;

  // properties of the data we will send
  const booleanProps = ['read'];

  const integerProps = ['timestamp'];

  const stringProps = ['id', 'title', 'text'];

  if (
    !notifications ||
    Object.prototype.toString.call(notifications) !== '[object Array]'
  ) {
    warn(
      'Notifications must be an array and provided when registering notification.'
    );
    return false;
  }

  if (!appId || Object.prototype.toString.call(appId) !== '[object String]') {
    warn('You must provide an appId when registering notifications.');
    return false;
  }

  // normalization and/or populate missing properties
  const len = notifications.length;

  if (len === 0) {
    warn('No notifications passed.');
    return false;
  }

  for (i = 0; i < len; i++) {
    current = notifications[i];

    for (j = 0; j < stringProps.length; j++) {
      if (
        current[stringProps[j]] &&
        typeof current[stringProps[j]] !== 'string'
      ) {
        warn(`Expected string for ${current[stringProps[j]]}, will convert.`);
        current[stringProps[j]] = current[stringProps[j]].toString() || '';
      } else if (Boolean(current[stringProps[j]]) === false) {
        warn('Expected string but no value found, will set to empty.');
        current[stringProps[j]] = '';
      }
    }

    for (j = 0; j < integerProps.length; j++) {
      if (
        current[integerProps[j]] &&
        typeof current[integerProps[j]] !== 'number'
      ) {
        warn(`Expected integer for ${current[integerProps[j]]}, will convert.`);
        current[integerProps[j]] = parseInt(current[integerProps[j]], 10) || 0;
      } else if (
        Boolean(current[integerProps[j]]) === false &&
        current[integerProps[j]] !== 0
      ) {
        warn('Expected integer but no value found, will set to 0.');
        current[integerProps[j]] = 0;
      }
    }

    for (j = 0; j < booleanProps.length; j++) {
      if (
        current[booleanProps[j]] !== 'undefined' &&
        current[booleanProps[j]] !== null &&
        typeof current[booleanProps[j]] !== 'boolean'
      ) {
        warn(`Expected boolean for ${current[booleanProps[j]]}, will convert.`);
        current[booleanProps[j]] = Boolean(current[booleanProps[j]]);
      } else if (
        current[booleanProps[j]] === 'undefined' ||
        current[booleanProps[j]] === null
      ) {
        warn('Expected boolean but no value found, setting to false.');
        current[booleanProps[j]] = false;
      }
    }

    if (
      typeof current.icon !== 'object' ||
      Object.prototype.toString.call(current.icon) === '[object Array]'
    ) {
      current.icon = {};
    }

    if (typeof current.icon.type === 'undefined') {
      current.icon.type = 'none';
    } else if (current.icon.type !== 'none') {
      warn(
        "Only supported value of 'icon.type' in registerAppNotifications is 'none'"
      );
      current.icon.type = 'none';
    }

    // purposeful == here for coercion
    // eslint-disable-next-line eqeqeq
    if (current.text == false && current.title == false) {
      warn('Must supply either a text or title property for notifications.');
      return false;
    }
  }

  // what we'll send along
  // we are not going to be bound by older conventions in our newer code.
  const data = {
    msg: 'MCLIB.REGISTER_APP_NOTIFICATIONS',
    object: notifications,
    appId
  };

  this.postMessageMC(data, callback, { stored: false });
};

MCLib.prototype.selectMenuItem = function(key, callback) {
  if (!key) {
    warn('You must provide the key of the item you want selected.');
    return;
  }

  const data = {
    msg: 'selectMenuItem',
    menuItemKey: key
  };

  this.postMessageMC(data, callback, { updatedMenu: false });
};

MCLib.prototype.getAppsInfo = function(callback) {
  const data = {
    msg: 'getAppsInfo'
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.getAppInfo = function(object, callback) {
  let errorMsg;
  const data = {
    msg: 'getAppInfo'
  };

  // we don't want both appId and appName, so error if they provide it
  if (object.appName && object.appId) {
    errorMsg = { error: 'appId or appName is required. Not both' };
  }

  if (!errorMsg) {
    // if there's no error, put the correct attribute on the data sent
    if (object.appName) {
      data.appName = object.appName;
    } else if (object.appId) {
      data.appId = object.appId;
    }

    // post message the ETMC
    this.postMessageMC(data, callback);
  } else {
    // otherwise callback with the error
    callback(errorMsg);
  }
};

MCLib.prototype.getBusinessRules = function(callback) {
  const data = {
    msg: 'getBusinessRules'
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.getBusinessRule = function(brName, callback) {
  let errorMsg;
  const data = {
    msg: 'getBusinessRule'
  };

  // must be a name!
  if (!brName || typeof brName !== 'string') {
    errorMsg = { error: 'You must provide a business rule name' };
  }

  if (!errorMsg) {
    // always lower-case the br names
    data.brName = brName.toLowerCase();

    this.postMessageMC(data, callback);
  } else {
    callback(errorMsg);
  }
};

MCLib.prototype.getUserKeyStore = function(callback) {
  const data = {
    msg: 'getUserKeyStore'
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.getUIExperienceSetting = function(callback) {
  const data = {
    msg: 'MCLIB.GET_UI_EXPERIENCE_SETTING' // new nameing convention for events
  };

  this.postMessageMC(data, callback);
};

MCLib.prototype.reloadApps = function(callback) {
  const data = {
    msg: 'reloadApps'
  };

  const fallbackResponse = {
    reloaded: false,
    apps: null
  };

  // this will always make a rest call so set the timeout to something more than 350ms
  this.postMessageMC(data, callback, fallbackResponse, 1000);
};

MCLib.prototype.unload = function() {
  this.on(
    'unload',
    ['marketing-cloud'],
    function() {
      if (this.unloadCallback) {
        this.unloadCallback(
          bind(function(resume) {
            if (resume) {
              this.postMessageMC({ msg: 'unloadResume' });
            } else {
              this.postMessageMC({ msg: 'unloadCancel' });
            }
          }, this)
        );
      } else {
        log('Unload event sent from marketing-cloud, but no callback found.');
        this.postMessageMC({ msg: 'unload-resume' });
      }
    },
    this
  );
};

MCLib.prototype.registerUnload = function(responseCallback, unloadCallback) {
  const data = {
    msg: 'appUnload'
  };

  if (unloadCallback) {
    this.unloadCallback = unloadCallback;
    this.postMessageMC(data, responseCallback);
  } else {
    responseCallback({ error: 'You must provide an unload callback' });
  }
};

MCLib.prototype.deregisterUnload = function(callback) {
  const data = {
    msg: 'appRemoveUnload'
  };

  // remove local callback
  this.unloadCallback = null;

  this.postMessageMC(data, callback);
};

MCLib.prototype.logoutEtmc = function() {
  this.postMessageMC({ msg: 'triggerLogout' });
};

MCLib.prototype.exitFullscreenApp = function(data) {
  this.postMessageMC({
    msg: 'exitFullscreenApp',
    options: data
  });
};

MCLib.prototype.disableNavigation = function(options) {
  const data = { msg: 'disableNavigation' };

  if (options) {
    if (options.opacity) {
      data.opacity = options.opacity;
    }
    if (options.backgroundColor) {
      data.backgroundColor = options.backgroundColor;
    }
  }

  this.postMessageMC(data);
};

MCLib.prototype.enableNavigation = function() {
  this.postMessageMC({ msg: 'enableNavigation' });
};

MCLib.prototype.getUserInfo = function(userInfoCallback) {
  const data = { msg: 'getUserInfo' };

  if (userInfoCallback) {
    this.postMessageMC(data, userInfoCallback);
  } else {
    userInfoCallback({ error: 'You must provide a callback' });
  }
};

MCLib.prototype.getPrivateLabelInfo = function(privateLabelCallback) {
  const data = {
    msg: 'getPrivateLabelInfo'
  };

  this.postMessageMC(data, privateLabelCallback, { cloudName: '' });
};

MCLib.prototype.requestRefetch = function(subModel, callback) {
  let errorMsg;
  const data = { msg: 'requestRefetch' };

  // must provide a submodel
  if (!subModel) {
    errorMsg = { error: 'You must provide a sub model' };
  }

  if (!errorMsg) {
    // always lower-case the br names
    data.subModel = subModel;

    this.postMessageMC(data, callback);
  } else if (callback) {
    callback(errorMsg);
  } else {
    warn(errorMsg);
  }
};

MCLib.prototype.registerLogoutUrl = function(url, callback) {
  let errorMsg;
  const data = { msg: 'registerLogoutUrl' };

  // must have a url
  if (!url) {
    errorMsg = { error: 'You must provide a URL' };
  }

  if (!errorMsg) {
    data.url = url;

    this.postMessageMC(data, callback);
  } else if (callback) {
    callback(errorMsg);
  } else {
    warn(errorMsg);
  }
};

MCLib.prototype.setApplicationUrl = function(url, callback) {
  let errorMsg;
  const data = { msg: 'setApplicationUrl' };

  // must have a url
  if (!url) {
    errorMsg = { error: 'You must provide a URL' };
  }

  if (!errorMsg) {
    data.url = url;

    if (this.currentAppData && this.currentAppData.applicationId) {
      data.appId = this.currentAppData.applicationId;
    }

    this.postMessageMC(data, callback);
  } else if (callback) {
    callback(errorMsg);
  } else {
    warn(errorMsg);
  }
};

MCLib.prototype.verifyAnalytics = function() {
  this.postMessageMC({ msg: 'verifyAnalytics' }, data => {
    data = data || {};
    localStorage.setItem(CONST.LOG_PERCENT_STORAGE_KEY, data.logPercent || 0);
    localStorage.setItem(CONST.ADRUM_URL, data.adrumUrl || '');
    localStorage.setItem(CONST.ADRUM_APP_KEY, data.adrumAppKey || '');
    this.analyticsVerified = true;
  });
};

MCLib.prototype.initGoogleAnalytics = function(propertyId) {
  if (!this.analyticsVerified) {
    setTimeout(this.initGoogleAnalytics.bind(this), 250, propertyId);
  } else {
    const logPercent = localStorage.getItem(CONST.LOG_PERCENT_STORAGE_KEY);
    const enableLogging = Boolean(logPercent && parseInt(logPercent, 10) >= 0);
    // initialize google analytics if the log percent is not negative
    if (enableLogging) {
      (function(i, s, o, g, r, a, m) {
        i.GoogleAnalyticsObject = r;
        (i[r] =
          i[r] ||
          function() {
            (i[r].q = i[r].q || []).push(arguments);
          }),
          (i[r].l = 1 * new Date());
        (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
        a.async = 1;
        a.src = g;
        m.parentNode.insertBefore(a, m);
      })(
        window,
        document,
        'script',
        '//www.google-analytics.com/analytics.js',
        'ga'
      );

      window.ga('create', propertyId);
      window.ga('set', 'forceSSL', true);
      window.ga('set', 'anonymizeIp', true);
    }
  }
};

MCLib.prototype.initAppDynamics = function() {
  if (!this.analyticsVerified) {
    setTimeout(this.initAppDynamics.bind(this), 250);
  } else {
    let scriptTag;
    const logPercent = localStorage.getItem(CONST.LOG_PERCENT_STORAGE_KEY);
    const adrumUrl = localStorage.getItem(CONST.ADRUM_URL);
    const adrumAppKey = localStorage.getItem(CONST.ADRUM_APP_KEY);
    const enableLogging = Boolean(
      logPercent && parseInt(logPercent, 10) >= Math.random() * 100
    );

    if (enableLogging && adrumUrl && adrumAppKey) {
      scriptTag = document.createElement('script');
      window['adrum-app-key'] = adrumAppKey;
      window['adrum-start-time'] = new Date().getTime();
      scriptTag.src = adrumUrl;
      document.getElementsByTagName('body')[0].appendChild(scriptTag);
    }
  }
};

MCLib.prototype.logEvent = function(category, value, eventCategory) {
  const data = {
    msg: 'logEvent',
    category,
    value,
    eventCategory
  };
  this.postMessageMC(data);
};

MCLib.prototype.logTiming = function(category, time, label) {
  time = parseInt(time, 10);

  if (isNaN(time)) {
    warn("'time' is not a number - timing not logged");
    return;
  }

  const data = {
    msg: 'logTiming',
    category,
    time,
    label
  };
  this.postMessageMC(data);
};

MCLib.prototype.startTiming = function(name) {
  this.timings = this.timings || {};

  if (window.performance && window.performance.now) {
    this.timings[name] = window.performance.now();
    if (window.performance.mark) {
      window.performance.mark(`${name}.start`);
    }
  } else {
    log('Date', Date.now());
  }
};

MCLib.prototype.endTiming = function(name) {
  let endTime;
  let totalTime;

  if (this.timings && this.timings[name]) {
    if (window.performance && window.performance.now) {
      endTime = window.performance.now();
      if (window.performance.mark) {
        window.performance.mark(`${name}.end`);
      }
    } else {
      endTime = Date.now();
    }

    totalTime = endTime - this.timings[name];
    this.logTiming(name, totalTime);

    delete this.timings[name];
  }
};

MCLib.prototype.logTimeSinceResponseEnd = function(name) {
  let items;
  if (window.performance && window.performance.measure) {
    window.performance.measure(name, 'responseEnd');

    items = window.performance.getEntriesByName(name);

    if (items) {
      this.logTiming(name, items[0].duration);
    }
  }
};

MCLib.prototype.initFTUX = function(enable, disable) {
  disable = disable || enable;

  this.on('ftuxStatus toggleFTUX', 'marketing-cloud', message => {
    const status = Boolean(
      (message && message.showFTUX) ||
        (message.data &&
          (typeof message.data.status === 'undefined' || message.data.status))
    );

    if (status) {
      enable(status, message);
    } else {
      disable(status, message);
    }
  });

  this.postMessageMC({ msg: 'initFTUX' });
  this.postMessageMC({ msg: 'ftuxStatus', getBrand: true });
};

function fuelAjax(message, url, data, callback) {
  let retVal = false;
  let err;

  if (typeof url !== 'string' || url.length === 0) {
    err = 'A url string must be provided';
  } else if (url.indexOf('https://') === 0 || url.indexOf('http://') === 0) {
    // as long as the url does not start with http:// or https://, MC-OS will
    // append the configured Fuel API host to the front of the provided url. We
    // only want to give mclib access to calling the Fuel API host
    err = 'Full urls cannot be provided. Only Fuel API paths are accepted.';
  }

  if (err) {
    if (typeof callback === 'function') {
      callback({ error: err });
    } else {
      warn(err);
    }
  } else {
    retVal = {
      msg: message,
      url
    };

    if (data) {
      retVal.data = data;
    }
  }

  return retVal;
}

MCLib.prototype.fuelApiGet = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.FUEL_API_GET', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.fuelApiGetPage = function(
  url,
  page,
  size,
  sortBy,
  sortDirection,
  filter,
  search,
  callback
) {
  const msg = fuelAjax('MCLIB.FUEL_API_GETPAGE', url, null, callback);

  if (msg) {
    msg.page = page;
    msg.size = size;
    msg.sortBy = sortBy;
    msg.sortDirection = sortDirection;
    msg.filter = filter;
    msg.search = search;

    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.fuelApiPost = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.FUEL_API_POST', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.fuelApiDelete = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.FUEL_API_DELETE', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.fuelApiPut = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.FUEL_API_PUT', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyApiGet = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.LEGACY_API_GET', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyApiGetPage = function(
  url,
  page,
  size,
  sortBy,
  sortDirection,
  filter,
  search,
  callback
) {
  const msg = fuelAjax('MCLIB.LEGACY_API_GETPAGE', url, null, callback);

  if (msg) {
    msg.page = page;
    msg.size = size;
    msg.sortBy = sortBy;
    msg.sortDirection = sortDirection;
    msg.filter = filter;
    msg.search = search;

    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyApiPost = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.LEGACY_API_POST', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyApiDelete = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.LEGACY_API_DELETE', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyApiPut = function(url, data, callback) {
  const msg = fuelAjax('MCLIB.LEGACY_API_PUT', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.fuelAjaxGet = function(url, data, callback) {
  const msg = fuelAjax('fuelAjaxGet', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.fuelAjaxPost = function(url, data, callback) {
  const msg = fuelAjax('fuelAjaxPost', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyAjaxGet = function(url, data, callback) {
  const msg = fuelAjax('legacyAjaxGet', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.legacyAjaxPost = function(url, data, callback) {
  const msg = fuelAjax('legacyAjaxPost', url, data, callback);

  if (msg) {
    this.postMessageMC(msg, callback);
  }
};

MCLib.prototype.sendMessage = function(message, data) {
  if (typeof message === 'string') {
    const send = {
      msg: 'MCLIB.SEND_MESSAGE', // flag for mc-os to decide what handler to use
      message // custom message sent from iframed app to UI
    };

    if (data) {
      send.data = data;
    }

    this.postMessageMC(send);
  }
};

MCLib.prototype.reloadMarketingCloud = function(hardReload) {
  const send = {
    msg: 'reloadMarketingCloud',
    hardReload: hardReload || false
  };

  this.postMessageMC(send);
};

export default MCLib;
